import { style, styleVariants } from "@vanilla-extract/css";

import { vars } from "~/styles/tokens.css";

// Shared base: everything every button wants regardless of variant.
// Variants below layer on top via vanilla-extract's styleVariants,
// which composes class names so the base rules always apply.
const base = style({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: vars.space.xs,

  fontFamily: vars.font.body,
  fontSize: vars.fontSize.sm,
  fontWeight: vars.fontWeight.semibold,
  lineHeight: vars.lineHeight.tight,

  borderRadius: vars.radius.lg,
  borderWidth: "1px",
  borderStyle: "solid",

  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",

  // Hover/active colour changes ease rather than snap. `fast` is the
  // control-feedback duration; prefers-reduced-motion collapses it
  // globally (styles/reduced-motion.css), so there is no guard here.
  transitionProperty: "background-color, border-color, color",
  transitionDuration: vars.motion.fast,
  transitionTimingFunction: vars.motion.easing,

  // Native buttons get focus outlines via the global base styles'
  // :focus-visible rule. We explicitly reset `outline: none` here
  // so the token-based shadow takes over — if we leave it, Chrome
  // draws its own outline on top.
  outline: "none",

  selectors: {
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
});

// Size axis, independent of variant. `md` is the comfortable default:
// the old sm/md padding combo (8/16) produced a button that read as
// "dense table action" rather than "primary CTA", so that treatment is
// now an explicit opt-in via `sm` — for console surfaces (action bars,
// row actions) where a 40px control would dominate the row.
export const buttonSize = styleVariants({
  md: {
    paddingBlock: "10px",
    paddingInline: vars.space.lg,
  },
  sm: {
    paddingBlock: vars.space.xs,
    paddingInline: vars.space.md,
  },
});

// Style variants — primary (filled brand), secondary (outline), and
// danger (filled red). Keep the set small until a real design need
// appears; adding a variant is cheap but removing one is expensive.
export const button = styleVariants({
  primary: [
    base,
    {
      backgroundColor: vars.color.primary,
      color: vars.color.primaryText,
      borderColor: vars.color.primary,
      selectors: {
        "&:hover:not(:disabled)": {
          backgroundColor: vars.color.primaryHover,
          borderColor: vars.color.primaryHover,
        },
        "&:active:not(:disabled)": {
          backgroundColor: vars.color.primaryActive,
          borderColor: vars.color.primaryActive,
        },
      },
    },
  ],
  secondary: [
    base,
    {
      backgroundColor: vars.color.secondary,
      color: vars.color.secondaryText,
      borderColor: vars.color.borderStrong,
      selectors: {
        "&:hover:not(:disabled)": {
          backgroundColor: vars.color.secondaryHover,
        },
        "&:active:not(:disabled)": {
          backgroundColor: vars.color.secondaryActive,
        },
      },
    },
  ],
  // Ghost — no surface until hovered. For actions that must be present
  // but must not compete: a row's Delete, a panel's dismiss.
  ghost: [
    base,
    {
      backgroundColor: "transparent",
      color: vars.color.textMuted,
      borderColor: "transparent",
      selectors: {
        "&:hover:not(:disabled)": {
          backgroundColor: vars.color.secondaryHover,
          color: vars.color.secondaryText,
        },
        "&:active:not(:disabled)": {
          backgroundColor: vars.color.secondaryActive,
        },
      },
    },
  ],
  danger: [
    base,
    {
      backgroundColor: vars.color.danger,
      color: vars.color.dangerText,
      borderColor: vars.color.danger,
      selectors: {
        "&:hover:not(:disabled)": {
          backgroundColor: vars.color.dangerHover,
          borderColor: vars.color.dangerHover,
        },
      },
    },
  ],
});
